import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { UserService } from '@services/user';
import { EventLoggerService } from '@services/event-logger-service';
import { LocalStorageService } from '@services/local-storage-service';
import { CsIconModule } from '@components/icon';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { AppConfig } from '../app.config';
import {
  CHAT_V2_ROUTE,
  CONSULTATION_SESSION_TYPE,
  DEFAULT_CONCERN_KEY,
  NAVIGATION_BACK_EVENT,
  TRUST_INFO_ASSET,
  TRUST_INFO_PROCEED_EVENT_NAME,
  TRUST_INFO_QUERY_PARAM,
  TRUST_INFO_SESSION_FIELD,
  TRUST_INFO_STAT,
  TRUST_INFO_USER_FIELD,
} from './trust-info.constant';

interface ConcernChip {
  label: string;
  highlighted: boolean;
}

interface TrustStat {
  value: string;
  label: string;
}

@Component({
  selector: 'trust-info-page',
  templateUrl: './trust-info-page.html',
  standalone: true,
  imports: [NgClass, CsIconModule, ButtonCtaComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrustInfoPageComponent implements OnInit {
  private readonly conn = inject(ConnectionService);
  private readonly broadcast = inject(BroadcastService);
  private readonly experimentService = inject(ExperimentService);
  private readonly userService = inject(UserService);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly appConfig = inject(AppConfig);
  private readonly localStorageService = inject(LocalStorageService);

  readonly heroImage = TRUST_INFO_ASSET.HERO_IMAGE;
  readonly leftIcon = TRUST_INFO_ASSET.MEN_TREATED_ICON;
  readonly rightIcon = TRUST_INFO_ASSET.IMPROVEMENT_ICON;

  readonly userName = signal('');
  readonly isDevnagriScript = signal(false);
  readonly chips = signal<ConcernChip[]>([]);
  readonly description = signal('');
  readonly leftStat = signal<TrustStat>({ value: '', label: '' });
  readonly rightStat = signal<TrustStat>({ value: '', label: '' });

  async ngOnInit(): Promise<void> {
    const concernClass = this.route.snapshot.queryParams[TRUST_INFO_QUERY_PARAM.CLASS] || this.appConfig.Shared.Regimen.Class.HAIR;

    const user = this.conn.getActingUser();
    if (!user) return;
    this.userName.set(user.get(TRUST_INFO_USER_FIELD.PATIENT_NAME)?.split(' ')?.[0] || '');

    const gender = user.get(TRUST_INFO_USER_FIELD.GENDER);
    const userLanguage = user.get(TRUST_INFO_USER_FIELD.LANGUAGE_PREFERENCE) || this.appConfig.Shared.Languages.EN;
    this.isDevnagriScript.set(this.userService.devnagriScripts.includes(userLanguage));
    const mainConcernField = concernClass === this.appConfig.Shared.Regimen.Class.FACE
      ? TRUST_INFO_USER_FIELD.FACE_MAIN_CONCERN
      : TRUST_INFO_USER_FIELD.HAIR_MAIN_CONCERN;
    const concernKey = user.get(mainConcernField) || user.get(TRUST_INFO_USER_FIELD.PRIVATE_MAIN_CONCERN);

    this.setStats(gender);

    await Promise.all([
      this.loadDescription(concernClass, gender, concernKey, userLanguage),
      this.loadChips(concernClass),
    ]);
  }

  private setStats(gender: string): void {
    if (gender === this.appConfig.Shared.Gender.FEMALE) {
      this.leftStat.set({ value: TRUST_INFO_STAT.WOMEN_TREATED, label: $localize`:@@trustInfoStatWomenTreated:Women Treated` });
      this.rightStat.set({ value: TRUST_INFO_STAT.FEMALE_IMPROVEMENT, label: $localize`:@@trustInfoStatImprovement:See Improvement` });
      return;
    }
    this.leftStat.set({ value: TRUST_INFO_STAT.MEN_TREATED, label: $localize`:@@trustInfoStatMenTreated:Men Treated` });
    this.rightStat.set({ value: TRUST_INFO_STAT.MALE_IMPROVEMENT, label: $localize`:@@trustInfoStatImprovement:See Improvement` });
  }

  private async loadDescription(concernClass: string, gender: string, concernKey: string, userLanguage: string): Promise<void> {
    await this.experimentService.experimentsLoaded;
    const variant = this.experimentService.getOnboardingTrustInfoVariant();
    const genderData = variant?.[concernClass]?.[gender];
    const entry = genderData?.[concernKey] || genderData?.[DEFAULT_CONCERN_KEY];
    const languageStringId = entry?.descriptionLanguageString;
    if (!languageStringId) return;

    try {
      const [languageString] = await this.conn.getLanguageStringsById([languageStringId]);
      this.description.set(languageString?.get(userLanguage) || languageString?.get(this.appConfig.Shared.Languages.EN) || '');
    } catch (error) {
      console.error('Error fetching trust-info description:', error);
    }
  }

  private async loadChips(concernClass: string): Promise<void> {
    const session = await this.conn.findConsultationSession(
      concernClass,
      [TRUST_INFO_SESSION_FIELD.PRIVATE_MAIN_CONCERN, TRUST_INFO_SESSION_FIELD.CONCERN_LIST],
    );
    const mainConcern = session?.get(TRUST_INFO_SESSION_FIELD.PRIVATE_MAIN_CONCERN);
    const secondaryConcerns = session?.get(TRUST_INFO_SESSION_FIELD.CONCERN_LIST) || [];
    // Keep the first occurrence of each concern so the main concern stays first (and highlighted).
    const concernKeys = [...new Set([mainConcern, ...secondaryConcerns].filter(Boolean))];
    if (!concernKeys.length) return;

    const labelByKey = await this.buildConcernLabelMap(concernClass);
    this.chips.set(concernKeys.map((key: string, index: number): ConcernChip => ({
      label: labelByKey[key] || this.humanizeConcernKey(key),
      highlighted: index === 0,
    })));
  }

  private async buildConcernLabelMap(concernClass: string): Promise<Record<string, string>> {
    const mainConcerns = await this.conn.findMainWithSecondaryConcerns({
      concernClass: concernClass as any,
      shouldTranslateConcernsList: true,
    });
    const labelByKey: Record<string, string> = {};
    mainConcerns.forEach((concern: any): void => {
      if (concern?.value) labelByKey[concern.value] = concern.displayName;
      (concern?.concernsList || []).forEach((secondary: any): void => {
        if (secondary?.value) labelByKey[secondary.value] = secondary.displayName;
      });
    });
    return labelByKey;
  }

  private humanizeConcernKey(key: string): string {
    return key
      .toLowerCase()
      .split('_')
      .map((word: string): string => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Back is deliberately non-destructive: it leaves the started tree alone and just returns to
   * concern selection, so a user who backs out without picking anything keeps a resumable session.
   * MainConcernComponent restarts the tree when they actually commit a new selection.
   */
  back(): void {
    this.broadcast.broadcast(NAVIGATION_BACK_EVENT);
  }

  proceed(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: TRUST_INFO_PROCEED_EVENT_NAME }));
    const queryParams = this.route.snapshot.queryParams;
    const sessionId = queryParams[TRUST_INFO_QUERY_PARAM.SESSION_ID];
    const chatQueryParams: Record<string, string> = {
      [TRUST_INFO_QUERY_PARAM.TYPE]: CONSULTATION_SESSION_TYPE,
      [TRUST_INFO_QUERY_PARAM.USERNAME]: queryParams[TRUST_INFO_QUERY_PARAM.USERNAME],
    };
    const redirectOnBack = queryParams[TRUST_INFO_QUERY_PARAM.REDIRECT_ON_BACK];
    if (redirectOnBack) chatQueryParams[TRUST_INFO_QUERY_PARAM.REDIRECT_ON_BACK] = redirectOnBack;
    this.router.navigate([`/${CHAT_V2_ROUTE}/${sessionId}`], { queryParams: chatQueryParams });
    // User has proceeded past concern selection: onboarding back should no longer return to class selection.
    this.localStorageService.delete('onboardingFlow');
  }
}
